import React, { useEffect, useMemo, useState } from 'react';
import propTypes from 'prop-types';
import CarrierDetailStates from 'tailwind-components/CarrierResources/CarrierDetailStates';
import ChipSection from './ChipSection';
import SelectedStateDetail from './SelectedStateDetail';
import { buildStateStatusMap, splitOepStateUpdates } from 'helpers/oepUpdates';

// left card of the oep updates grid: expansions + exits chips on the left, the
// selected state's counties panel on the right, and the full "all available
// states" grid below
function CarrierUpdatesPanel({ carrier }) {
  const [selectedKey, setSelectedKey] = useState(null);

  const { expansions, exits } = useMemo(
    () => splitOepStateUpdates(carrier),
    [carrier]
  );

  // reset the selection whenever the visible carrier changes so we don't keep a
  // stale highlight from a previous carrier's chip
  useEffect(() => {
    setSelectedKey(null);
  }, [carrier?.id]);

  const { codes: allStateCodes, statusByCode } = useMemo(
    () => buildStateStatusMap(carrier),
    [carrier]
  );

  const selectedUpdate = useMemo(() => {
    if (!selectedKey) return null;
    return (
      [...expansions, ...exits].find(u => u.id === selectedKey) || null
    );
  }, [selectedKey, expansions, exits]);

  function makeChipHandler(update) {
    return () =>
      setSelectedKey(prev => (prev === update.id ? null : update.id));
  }

  const hasUpdates = expansions.length > 0 || exits.length > 0;
  const hasStates = allStateCodes.length > 0;

  if (!hasUpdates && !hasStates) {
    return (
      <div
        data-tour='oep-updates-panel'
        className='flex min-h-[200px] flex-col rounded-xl border border-grey-lightest bg-white p-4'
      >
        <h3 className='m-0 mb-3 text-xl font-semibold text-black'>
          Carrier Updates
        </h3>
        <div className='flex flex-1 items-center justify-center text-sm text-gray-400'>
          No state expansions or exits published for this carrier yet.
        </div>
      </div>
    );
  }

  return (
    <div
      data-tour='oep-updates-panel'
      className='flex min-h-0 flex-col gap-4 rounded-xl border border-grey-lightest bg-white p-4'
    >
      <h3 className='m-0 text-xl font-semibold text-black'>Carrier Updates</h3>

      {hasUpdates && (
        <div className='flex flex-col gap-4'>
          <ChipSection
            title='Expansions'
            helper='Click the state below to view county expansions'
            updates={expansions}
            statusByCode={statusByCode}
            selectedKey={selectedKey}
            makeHandler={makeChipHandler}
            emptyText='No expansions this OEP.'
          />
          <ChipSection
            title='Exits'
            helper='Click the state below to view county exits'
            updates={exits}
            statusByCode={statusByCode}
            selectedKey={selectedKey}
            makeHandler={makeChipHandler}
            emptyText='No exits this OEP.'
          />
          {selectedUpdate && (
            <SelectedStateDetail
              update={selectedUpdate}
              aggregatedStatus={statusByCode[selectedUpdate.stateCode]}
            />
          )}
        </div>
      )}

      {hasStates && (
        <>
          <hr className='m-0 border-dashed border-gray-200' />
          <CarrierDetailStates
            states={allStateCodes}
            carrierName='All Available States'
            ignoreSbeAndFfmLabels
            abbreviated
            stateStatusMap={statusByCode}
          />
        </>
      )}
    </div>
  );
}

CarrierUpdatesPanel.propTypes = {
  carrier: propTypes.object.isRequired
};

export default CarrierUpdatesPanel;
